import React from "react";
import "../css/Program.css";

// Safely resolve assets from src or public
function resolveAsset(name) {
  try {
    const req = require.context("../", true, /\.(svg|png|jpe?g)$/);
    const match = req
      .keys()
      .find((k) => k.toLowerCase().includes(name.toLowerCase()));
    if (match) {
      const res = req(match);
      return res.default || res;
    }
  } catch (e) {
    // ignore
  }
  return `${process.env.PUBLIC_URL || ""}/${name}`;
}

const onionImg = resolveAsset("onion");
const termcardImg = resolveAsset("term");

function Program() {
  return (
    <div className="program-page-wrapper">
      {/* Section 1: Science Communication */}
      <div className="program-intro-section">
        <div className="program-container">
          <div className="program-grid-intro">
            <div className="program-intro-main">
              <span className="program-eyebrow">PROGRAMME</span>
              <h1 className="program-main-title">
                Science
                <br />
                Communication
              </h1>
              <div className="program-yellow-bar" />

              <p className="program-subhead">What Is Science Communication?</p>
              <p className="program-body-p">
                Science communication is all about sharing science in ways that
                resonate—whether through words, visuals, video, or conversation.
                It's not just about what you say, but how, where, and to whom you
                say it. Whether you're explaining your research to a curious
                six-year-old or presenting at a high-level academic conference, the
                way you communicate matters.
              </p>

              <p className="program-body-p">
                At its heart, effective science communication is audience-centered.
                That's why we broadly divide it into two key categories:
              </p>
            </div>

            <aside className="program-intro-aside">
              {/* Beige box for Lay & Expert Audiences */}
              <div className="program-audience-box">
                <div>
                  <p className="audience-heading">Lay Audience</p>
                  <p className="audience-desc">
                    The general public, including non-specialists and curious minds
                    outside your field.
                  </p>
                </div>
                <div>
                  <p className="audience-heading">Expert Audience</p>
                  <p className="audience-desc">
                    Fellow researchers, academics, and professionals in your
                    discipline.
                  </p>
                </div>
              </div>

              <p className="program-body-p">
                Adapting your message to suit your audience is essential. The
                setting—be it a conference, a classroom, a tweet, or a casual chat
                over coffee—along with the medium you use—like text, video, visuals,
                or data—shapes how your message is received and understood.
              </p>

              <p className="program-body-p" style={{ marginBottom: 0 }}>
                Crafting a message that's clear, compelling, and accurate takes more
                than knowledge—it takes skill, creativity, and practice. That's
                where OSSCE comes in. We offer high-quality training and support to
                help you build your communication skills, grow your confidence, and
                connect with your audience more effectively—whether you're talking
                to one person or a thousand.
              </p>
            </aside>
          </div>
        </div>
      </div>

      {/* Section 2: Public Engagement (Full Bleed Dark Navy) */}
      <div className="program-public-section">
        <div className="program-container">
          <div className="program-public-grid">
            <div className="program-public-header">
              <span className="program-eyebrow">PROGRAMME</span>
              <h2 className="program-public-title">
                Public
                <br />
                engagement
              </h2>

              <p className="program-public-subhead">
                What is Public Engagement?
              </p>
              <p className="program-public-italic">
                ...it's all abour onions...
              </p>
            </div>

            {/* Onion diagram properly sized inside a white card */}
            <div className="program-onion-box">
              <img
                src={onionImg || "../assets/images/program-onion.png"}
                alt="Wellcome Trust Onion Model Diagram"
                className="program-onion-img"
                onError={(e) => {
                  // Try direct relative path if dynamic resolution failed
                  if (!e.currentTarget.dataset.retried) {
                    e.currentTarget.dataset.retried = "true";
                    e.currentTarget.src = "../assets/images/program-onion.png";
                  }
                }}
              />
            </div>

            <div className="program-public-body">
              <p className="program-public-p">
                Public engagement is essential for ensuring that scientific
                research has a meaningful impact beyond academia. The Wellcome
                Trust's Onion Model provides a useful framework for
                understanding different levels of engagement. At its core, the
                model stresses the importance of moving beyond traditional
                "one-way" communication, where researchers simply share
                information, to more collaborative, two-way dialogues. These
                interactions, based on mutual respect and understanding, should
                happen on equal terms between scientists and stakeholders,
                rather than positioning one as the expert and the other as
                passive recipients of knowledge.
              </p>

              <p className="program-public-p">
                The model emphasizes two poles: informing and dialogue. While
                informing is a necessary part of public engagement, true impact
                comes when engagement shifts toward dialogue, where all
                participants—whether researchers, policymakers, or the
                public—contribute to shaping the conversation. This approach
                ensures that public engagement is inclusive, empowering
                stakeholders and fostering a deeper understanding of science's
                relevance in society. Engaging on equal terms enriches both
                scientific research and public discourse.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Section 3: Our Termcard */}
      <div className="program-termcard-section">
        <div className="program-container">
          <span className="program-eyebrow">MT 26</span>
          <h2 className="program-termcard-title">Our Termcard</h2>

          {/* <div className="program-termcard-card">
            <img
              src={termcardImg || "../assets/images/program-term card.png"}
              alt="Our Termcard Timeline"
              className="program-termcard-img"
              onError={(e) => {
                // Try alternate path if not loaded
                if (!e.currentTarget.dataset.retried) {
                  e.currentTarget.dataset.retried = "true";
                  e.currentTarget.src = "../assets/images/program-term card.png";
                } else {
                  e.currentTarget.style.display = "none";
                  if (e.currentTarget.nextSibling) {
                    e.currentTarget.nextSibling.style.display = "flex";
                  }
                }
              }}
            />

            <div
              style={{
                display: "none",
                width: "100%",
                alignItems: "center"
              }}
            >
              <div className="termcard-fallback-badge">MICHAELMAS TERM 2025</div>
              <div className="termcard-fallback-weeks">
                <div className="termcard-fallback-pill">Week 1</div>
                <div className="termcard-fallback-pill light">Week 2</div>
                <div className="termcard-fallback-pill">Week 3: Science Communication & Social Media</div>
                <div className="termcard-fallback-pill light">Week 4</div>
                <div className="termcard-fallback-pill">Week 5: Workshop: Introduction to Public Engagement</div>
                <div className="termcard-fallback-pill light">Week 6</div>
                <div className="termcard-fallback-pill">Week 7: Panel: How to become a Science Journalist</div>
                <div className="termcard-fallback-pill muted">Week 8</div>
              </div>
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
}

export default Program;
